/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import React, { Fragment, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import BalanceSegment from 'components/layouts/Bar/Balance/BalanceSegment/BalanceSegment';
import { compose } from 'redux';

import Content from 'services/Content';
import DisclosureModal from 'components/shared/Disclosure/DisclosureModal';

import StandardBalanceBar from './StandardBalanceBar';
import { getYTDBalanceData } from 'services/Rest';
import Time from 'services/Time';
import { isReceived } from 'support/RequestSupport';
import { withOtelContext } from '@trp-ta-nitro/otel-react';
import otapi from '@opentelemetry/api';

const PortfolioLabel = () => (
  <div className="portfolio-label">Does not include external accounts</div>
);

const dailyChangeLabel = 'Daily Change';
const yearToDateChange = 'Year-To-Date Change';

const TotalPortfolio = ({ dollarChange, is529Only }) => {
  return (
    <div className={'bar balance'}>
      <BalanceSegment
        label="Total Portfolio"
        dollarChange={dollarChange}
        onlyBalance={true}
        is529Only={is529Only}
        tooltipContent={Content.TotalPortfolioContent}
        extraContent={<PortfolioLabel />}
      />
    </div>
  );
};

const DailyAndYearToDateChange = ({
  label,
  dollarChange,
  rateChange,
  isAlaska529Only
}) => {
  return isEqual(label, dailyChangeLabel) ? (
    <div className={'bar balance'}>
      <BalanceSegment
        label={dailyChangeLabel}
        dollarChange={dollarChange}
        rateChange={rateChange}
        isAlaska529Only={isAlaska529Only}
      />
    </div>
  ) : (
    <BalanceSegment
      label={yearToDateChange}
      dollarChange={dollarChange}
      rateChange={rateChange}
      isAlaska529Only={isAlaska529Only}
    />
  );
};

export const TotalBalanceDisclosure = ({ is529Only }) => {
  return (
    <DisclosureModal
      className="total-balance-disclosure"
      content={
        is529Only
          ? Content.asOfDateDisclosure529Only
          : Content.asOfDateDisclosure
      }
      title="Important Information Regarding the Updating of Account Balances"
      label="total-balance-disclosure"
      hideLinkContent
    />
  );
};

export const BalanceBarPortfolioDailyYearlyContainer = ({
  addAccountBalanceClass,
  canViewYTDChange,
  hasWprAccounts,
  balances,
  is529Only,
  hasAlaska529Only,
  otelContext,
  totalPortfolio,
  totalDailyChange,
  totalDailyRate,
  yearlyChange,
  yearlyRate,
  accountChangeAccounts,
  asOfDate
}) => {
  const { aggregate } = balances;

  useEffect(() => {
    if (
      !isReceived('balances') &&
      !isNil(asOfDate) &&
      !isNil(accountChangeAccounts)
    ) {
      otapi.context.with(otelContext, () => {
        getYTDBalanceData(Time.startOfYear(), asOfDate, accountChangeAccounts);
      });
    }
  }, [asOfDate, accountChangeAccounts, otelContext]);

  return (
    <div
      className={`balance-bar-portfolio-daily-yearly-container ${addAccountBalanceClass}`}>
      <div className={`total-portfolio-container ${addAccountBalanceClass}`}>
        {!hasWprAccounts && (
          <TotalPortfolio dollarChange={aggregate} is529Only={is529Only} />
        )}
        {hasWprAccounts && !is529Only && (
          <TotalPortfolio dollarChange={totalPortfolio} is529Only={is529Only} />
        )}
        {hasWprAccounts && is529Only && (
          <TotalPortfolio dollarChange={aggregate} is529Only={is529Only} />
        )}
      </div>

      <div
        className={`daily-change-year-to-date-container ${addAccountBalanceClass}`}>
        {!hasWprAccounts && (
          <Fragment>
            <DailyAndYearToDateChange
              label={dailyChangeLabel}
              dollarChange={totalDailyChange}
              rateChange={totalDailyRate}
            />
            {canViewYTDChange && (
              <DailyAndYearToDateChange
                label={yearToDateChange}
                dollarChange={yearlyChange}
                rateChange={yearlyRate}
              />
            )}
          </Fragment>
        )}
        {hasWprAccounts && !is529Only && (
          <Fragment>
            <DailyAndYearToDateChange
              label={dailyChangeLabel}
              dollarChange={totalDailyChange}
              rateChange={totalDailyRate}
            />
            {canViewYTDChange && (
              <DailyAndYearToDateChange
                label={yearToDateChange}
                dollarChange={yearlyChange}
                rateChange={yearlyRate}
              />
            )}
          </Fragment>
        )}
        {hasWprAccounts && is529Only && (
          <Fragment>
            <DailyAndYearToDateChange
              label={dailyChangeLabel}
              dollarChange={totalDailyChange}
              rateChange={totalDailyRate}
              isAlaska529Only={hasAlaska529Only}
            />
            {canViewYTDChange && (
              <DailyAndYearToDateChange
                label={yearToDateChange}
                dollarChange={yearlyChange}
                rateChange={yearlyRate}
              />
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
};

const BalanceBar = ({ isBalanceBarDataLoading }) => (
  <StandardBalanceBar isBalanceBarDataLoading={isBalanceBarDataLoading} />
);

export default compose(withOtelContext, withRouter)(BalanceBar);
